import React, { useEffect, useState } from 'react';
import { db, storage, isStorageAvailable } from '../../../firebase';
import { doc, getDoc, setDoc, collection, Timestamp } from 'firebase/firestore';
import { ref, uploadBytes, getDownloadURL, deleteObject } from 'firebase/storage';
import { useNavigate } from 'react-router-dom';
import ClientLayout from '../../../components/layout/ClientLayout';
import { useEmailVerification } from '../../../hooks/useEmailVerification';
import { useAuth } from '../../../hooks/useAuth';

interface UserInfo {
  name: string;
  phoneNumber: string;
  email: string;
  organization: string;
  userLevel: number;
}

interface UploadedFile {
  name: string;
  url: string;
  type: 'identification' | 'employment' | 'other';
  path: string;
  file: File;
}

// 예약 신청 인터페이스 추가
interface PortvilleApplication {
  uid: string;
  name: string;
  email: string;
  phoneNumber: string;
  organization: string;
  userLevel: number;
  additionalRequest: string;
  documents: {
    identification: string;
    employment: string;
    other?: string;
  };
  status: 'pending' | 'approved' | 'rejected';
  createdAt: Timestamp;
  updatedAt: Timestamp;
}

// 업체분류 옵션 상수 추가
const ORGANIZATION_TYPES = [
  { value: 9, label: '일반사용자' },
  { value: 8, label: '항만근로자' },
  { value: 7, label: '공공기관' },
  { value: 6, label: '입주협력업체' },
  { value: 5, label: '입주업체' }
] as const;

const WaitingPage: React.FC = () => {
  const navigate = useNavigate();
  const { user, loading: authLoading } = useAuth();
  const [currentStep, setCurrentStep] = useState<number>(1);
  const [privacyAgreed, setPrivacyAgreed] = useState<boolean>(false);
  const [userInfo, setUserInfo] = useState<UserInfo | null>(null);
  const [selectedOrgType, setSelectedOrgType] = useState<number | null>(null);
  const [additionalRequest, setAdditionalRequest] = useState<string>('');
  const [loading, setLoading] = useState<boolean>(true);
  const [error, setError] = useState<string | null>(null);
  const [uploadedFiles, setUploadedFiles] = useState<UploadedFile[]>([]);
  const [uploading, setUploading] = useState<boolean>(false);
  const [uploadError, setUploadError] = useState<string | null>(null);
  const [submitting, setSubmitting] = useState<boolean>(false);
  const [storageAvailable, setStorageAvailable] = useState<boolean>(true);
  const { loading: emailVerificationLoading } = useEmailVerification();

  // 스토리지 서비스 가용성 확인
  useEffect(() => {
    const checkStorage = async () => {
      const available = await isStorageAvailable();
      setStorageAvailable(available);
      if (!available) {
        console.warn('Firebase Storage를 사용할 수 없습니다. 모의 업로드를 사용합니다.');
      }
    };
    
    checkStorage();
  }, []);

  useEffect(() => {
    const fetchUserInfo = async () => {
      // authLoading이 완료될 때까지 대기
      if (authLoading) {
        return;
      }

      // 인증 로딩이 완료된 후 user가 없으면 로그인 페이지로 이동
      if (!user) {
        navigate('/signin');
        return;
      }

      try {
        const userDocRef = doc(db, 'user_profiles', user.uid);
        const userDocSnap = await getDoc(userDocRef);

        if (userDocSnap.exists()) {
          const userData = userDocSnap.data();
          setUserInfo({
            name: userData.name,
            phoneNumber: userData.phoneNumber,
            email: userData.email,
            organization: userData.organization,
            userLevel: userData.userLevel
          });
        } else {
          setError('사용자 정보를 찾을 수 없습니다.');
        }
      } catch (err) {
        console.error('사용자 정보 로딩 오류:', err);
        setError('사용자 정보를 불러오는 중 오류가 발생했습니다.');
      } finally {
        setLoading(false);
      }
    };

    fetchUserInfo();
  }, [user, authLoading, navigate]);

  const getUserLevelText = (level: number): string => {
    switch (level) {
      case 1: return '최고 관리자';
      case 2: return '일반 관리자';
      case 3: return '공사 관계자';
      case 4: return '센터 관계자';
      case 5: return '입주업체';
      case 6: return '입주협력업체';
      case 7: return '공공기관';
      case 8: return '항만근로자';
      case 9: return '일반 사용자';
      default: return '일반 사용자';
    }
  };

  const renderUploadedFileStatus = (type: UploadedFile['type']) => {
    const uploadedFile = uploadedFiles.find(file => file.type === type);

    if (!uploadedFile) {
      return (
        <div className="mt-3 rounded-sm border border-dashed border-gray-300 bg-gray-50 px-4 py-3">
          <p className="text-sm text-gray-500">아직 업로드된 파일이 없습니다.</p>
        </div>
      );
    }

    return (
      <div className="mt-3 rounded-sm border border-emerald-200 bg-emerald-50 px-4 py-3">
        <div className="flex flex-col gap-2 sm:flex-row sm:items-start sm:justify-between">
          <div className="min-w-0">
            <p className="text-xs font-semibold text-emerald-700">현재 업로드된 파일</p>
            <p className="mt-1 break-all text-sm font-medium text-slate-800">{uploadedFile.name}</p>
          </div>
          <span className="inline-flex w-fit items-center rounded-full bg-white px-3 py-1 text-xs font-semibold text-emerald-700">
            업로드 완료
          </span>
        </div>
        <p className="mt-2 text-xs text-emerald-700">다른 파일을 선택하면 기존 파일이 새 파일로 교체됩니다.</p>
      </div>
    );
  };

  const handleFileUpload = async (file: File, type: 'identification' | 'employment' | 'other', inputElement?: HTMLInputElement) => {
    if (!user) {
      alert('로그인이 필요합니다.');
      return;
    }

    try {
      setUploading(true);
      setUploadError(null);

      // 파일 크기 체크
      if (file.size > 5 * 1024 * 1024) {
        alert('파일 크기는 5MB를 초과할 수 없습니다.');
        // input 초기화
        if (inputElement) {
          inputElement.value = '';
        }
        return;
      }

      const timestamp = Date.now();
      const fileName = `${user.uid}_${type}_${timestamp}_${file.name}`;
      const filePath = `waiting_documents/${user.uid}/temp/${fileName}`;
      
      const storageRef = ref(storage, filePath);
      const metadata = {
        contentType: file.type,
        customMetadata: {
          createdAt: new Date().toISOString(),
          type: type
        }
      };

      await uploadBytes(storageRef, file, metadata);
      const url = await getDownloadURL(storageRef);

      // 업로드된 파일 정보 상태 업데이트
      setUploadedFiles(prev => {
        const filtered = prev.filter(f => f.type !== type);
        return [...filtered, {
          name: file.name,
          url: url,
          type: type,
          path: filePath,
          file
        }];
      });
    } catch (error) {
      console.error('파일 업로드 중 오류 발생:', error);
      setUploadError('파일 업로드 중 오류가 발생했습니다.');
    } finally {
      setUploading(false);
    }
  };

  const handleSubmit = async () => {
    if (!userInfo || !user) {
      alert('사용자 정보가 없습니다. 다시 로그인해주세요.');
      return;
    }

    try {
      setSubmitting(true);

      // 필수 파일 확인
      const identificationFile = uploadedFiles.find(f => f.type === 'identification');
      const employmentFile = uploadedFiles.find(f => f.type === 'employment');

      if (!identificationFile || !employmentFile) {
        alert('필수 서류가 업로드되지 않았습니다.');
        return;
      }

      // 일반 사용자인 경우 업체분류 선택 필수
      if (userInfo.userLevel === 9 && !selectedOrgType) {
        alert('업체분류를 선택해주세요.');
        setSubmitting(false);
        return;
      }

      const applicationsRef = collection(db, 'portville_applications');
      const docRef = doc(applicationsRef);
      const finalizedDocuments: PortvilleApplication['documents'] = {
        identification: '',
        employment: ''
      };

      for (const uploadedFile of uploadedFiles) {
        const timestamp = Date.now();
        const finalizedFileName = `${user.uid}_${uploadedFile.type}_${timestamp}_${uploadedFile.name}`;
        const finalizedPath = `waiting_documents/${docRef.id}/submitted/${finalizedFileName}`;

        await uploadBytes(ref(storage, finalizedPath), uploadedFile.file, {
          contentType: uploadedFile.file.type,
          customMetadata: {
            createdAt: new Date().toISOString(),
            type: uploadedFile.type,
            migratedFrom: uploadedFile.path
          }
        });

        finalizedDocuments[uploadedFile.type] = finalizedPath;

        if (uploadedFile.path.includes('/temp/')) {
          await deleteObject(ref(storage, uploadedFile.path)).catch((deleteError) => {
            console.warn('임시 파일 삭제 실패:', uploadedFile.path, deleteError);
          });
        }
      }

      // 신청서 데이터 생성
      const now = Timestamp.now();
      const applicationData: PortvilleApplication = {
        uid: user.uid,
        name: userInfo.name,
        email: userInfo.email,
        phoneNumber: userInfo.phoneNumber,
        organization: userInfo.organization,
        userLevel: userInfo.userLevel === 9 ? selectedOrgType! : userInfo.userLevel,
        additionalRequest: additionalRequest,
        documents: finalizedDocuments,
        status: 'pending',
        createdAt: now,
        updatedAt: now
      };

      await setDoc(docRef, applicationData);

      // 완료 메시지 표시
      alert('입주 신청이 완료되었습니다. 심사 후 결과를 알려드립니다.');
      
      // 신청 내역 페이지로 이동
      navigate('/portville/detail');
    } catch (err) {
      console.error('신청서 제출 오류:', err);
      alert('신청서 제출 중 오류가 발생했습니다. 다시 시도해주세요.');
    } finally {
      setSubmitting(false);
    }
  };

  const handleNext = () => {
    if (currentStep === 1 && isFirstStepValid()) {
      setCurrentStep(2);
    } else if (currentStep === 2 && isSecondStepValid()) {
      setCurrentStep(3);
    }
  };

  const handlePrevious = () => {
    if (currentStep > 1) {
      setCurrentStep(currentStep - 1);
    }
  };

  const isFirstStepValid = (): boolean => {
    if (!privacyAgreed || !userInfo) return false;

    // 일반 사용자인 경우 업체분류 선택 필수
    if (userInfo.userLevel === 9 && !selectedOrgType) return false;

    return Boolean(
      userInfo.name && 
      userInfo.phoneNumber && 
      userInfo.email && 
      userInfo.organization
    );
  };

  const isSecondStepValid = (): boolean => {
    const hasIdentification = uploadedFiles.some(file => file.type === 'identification');
    const hasEmployment = uploadedFiles.some(file => file.type === 'employment');
    return hasIdentification && hasEmployment;
  };

  // 업체분류 선택 핸들러 추가
  const handleOrgTypeChange = (e: React.ChangeEvent<HTMLSelectElement>) => {
    setSelectedOrgType(Number(e.target.value));
  };

  if (authLoading || loading || emailVerificationLoading) {
    return (
      <ClientLayout>
        <div className="flex items-center justify-center min-h-screen">
          <div className="text-lg">로딩중...</div>
        </div>
      </ClientLayout>
    );
  }

  if (error) {
    return (
      <ClientLayout>
        <div className="flex items-center justify-center min-h-screen">
          <div className="text-red-600">{error}</div>
        </div>
      </ClientLayout>
    );
  }

  return (
    <ClientLayout>
      <div className="w-full min-h-screen bg-white">
        {/* 본문 컨테이너 - 중앙 정렬 */}
        <div className="max-w-3xl w-full mx-auto px-4 sm:px-6 lg:px-8 relative pt-8">
          {/* 헤더 섹션 */}
          <div className="mt-8">
            <div className="text-slate-800 text-2xl sm:text-3xl md:text-4xl font-extrabold font-['Pretendard'] leading-tight sm:leading-9">
              포트빌 입주 신청
            </div>
            <div className="mt-4 flex flex-col sm:flex-row sm:items-center sm:justify-between gap-2">
              <div className="text-slate-700 text-base sm:text-lg md:text-xl font-normal font-['Pretendard'] leading-relaxed">
                입주 신청을 위해 정보를 입력해주세요
              </div>
              <div className="flex items-center">
                <span className="text-slate-700 text-base sm:text-lg md:text-xl font-medium font-['Pretendard'] leading-9 mr-2">단계</span>
                <span className="text-sky-600 text-base sm:text-lg md:text-xl font-medium font-['Pretendard'] leading-9">{currentStep} </span>
                <span className="text-slate-400 text-base sm:text-lg md:text-xl font-medium font-['Pretendard'] leading-9">/ 3</span>
              </div>
            </div>
          </div>

          {/* 구분선 */}
          <div className="w-full h-px mt-8 outline outline-1 outline-offset-[-0.50px] outline-neutral-200"></div>
          
          {/* 1단계: 개인정보 및 기본 정보 입력 */}
          {currentStep === 1 && (
            <>
              {/* 개인정보 수집 동의 섹션 */}
              <div className="mt-8">
                <div className="flex items-center space-x-2 mb-4">
                  <input
                    type="checkbox"
                    id="privacyAgreement"
                    checked={privacyAgreed}
                    onChange={(e) => setPrivacyAgreed(e.target.checked)}
                    className="w-4 h-4 rounded border-gray-300 focus:ring-0 checked:bg-gray-500 checked:border-gray-500 [&:checked]:bg-gray-500 [&:checked]:border-gray-500"
                  />
                  <label htmlFor="privacyAgreement" className="text-slate-800 text-sm sm:text-base font-bold font-['Pretendard']">
                    개인정보 수집 및 이용동의
                  </label>
                </div>
                <div className="w-full opacity-90 bg-white rounded-sm outline outline-1 outline-gray-300">
                  <div className="p-4">
                    <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-2">
                      <div className="text-[#42506e] text-sm sm:text-base font-normal font-['Pretendard'] leading-relaxed">
                        입력하신 정보는 예약 처리 및 고객 응대 목적으로만 사용되며, 외부로 제공되지 않습니다.
                      </div>
                      <div className="text-blue-500 text-sm sm:text-base font-medium font-['Pretendard'] underline leading-relaxed cursor-pointer whitespace-nowrap">
                        자세히보기
                      </div>
                    </div>
                  </div>
                </div>
              </div>

              {/* 신청자 정보 섹션 */}
              <div className="mt-8">
                <div className="text-slate-800 text-lg sm:text-xl font-bold font-['Pretendard'] leading-9">
                  신청자 정보
                </div>
                <div className="mt-4 w-full bg-gray-50 rounded border border-black/5 p-4 sm:p-6 md:p-8">
                  <div className="grid grid-cols-1 sm:grid-cols-2 gap-4 sm:gap-6 md:gap-8">
                    <div>
                      <div className="text-gray-800 text-sm sm:text-base font-semibold font-['Pretendard'] leading-9 mb-2">
                        이름
                      </div>
                      <div className="w-full h-12 bg-white rounded-sm outline outline-[0.50px] outline-gray-300 flex items-center px-4">
                        <div className="text-gray-400 text-sm sm:text-base font-medium font-['Pretendard'] leading-relaxed">
                          {userInfo?.name || '이름을 입력해주세요'}
                        </div>
                      </div>
                    </div>

                    <div>
                      <div className="text-gray-800 text-sm sm:text-base font-semibold font-['Pretendard'] leading-9 mb-2">
                        연락처
                      </div>
                      <div className="w-full h-12 bg-white rounded-sm outline outline-[0.50px] outline-gray-300 flex items-center px-4">
                        <div className="text-gray-400 text-sm sm:text-base font-medium font-['Pretendard'] leading-relaxed">
                          {userInfo?.phoneNumber || '전화번호를 입력해주세요'}
                        </div>
                      </div>
                    </div>

                    <div className="sm:col-span-2">
                      <div className="text-gray-800 text-sm sm:text-base font-semibold font-['Pretendard'] leading-9 mb-2">
                        이메일
                      </div>
                      <div className="w-full h-12 bg-white rounded-sm outline outline-[0.50px] outline-gray-300 flex items-center px-4">
                        <div className="text-gray-400 text-sm sm:text-base font-medium font-['Pretendard'] leading-relaxed">
                          {userInfo?.email || '이메일 주소를 입력해주세요'}
                        </div>
                      </div>
                    </div>
                  </div>
                </div>
                <div className="mt-4 text-[#42506e] text-xs sm:text-sm font-normal font-['Pretendard'] leading-relaxed">
                  * 개인정보를 확인하시고, 수정이 필요하시면 설정 메뉴를 이용해주세요
                </div>
              </div>

              {/* 업체 정보 섹션 */}
              <div className="mt-8">
                <div className="text-slate-800 text-lg sm:text-xl font-bold font-['Pretendard'] leading-9">
                  업체 정보
                </div>
                <div className="mt-4 w-full bg-gray-50 rounded border border-black/5 p-4 sm:p-6 md:p-8">
                  <div className="grid grid-cols-1 sm:grid-cols-2 gap-4 sm:gap-6 md:gap-8">
                    <div>
                      <div className="text-gray-800 text-sm sm:text-base font-semibold font-['Pretendard'] leading-9 mb-2">
                        업체분류
                      </div>
                      {userInfo?.userLevel === 9 ? (
                        <select
                          value={selectedOrgType || ''}
                          onChange={handleOrgTypeChange}
                          className="w-full h-12 bg-white rounded-sm outline outline-[0.50px] outline-gray-300 px-4 text-sm sm:text-base text-gray-700"
                        >
                          <option value="">업체분류를 선택해주세요</option>
                          {ORGANIZATION_TYPES.map((type) => (
                            <option key={type.value} value={type.value}>
                              {type.label}
                            </option>
                          ))}
                        </select>
                      ) : (
                        <div className="w-full h-12 bg-white rounded-sm outline outline-[0.50px] outline-gray-300 flex items-center px-4">
                          <div className="text-gray-400 text-sm sm:text-base font-medium font-['Pretendard'] leading-relaxed">
                            {getUserLevelText(userInfo?.userLevel || 0)}
                          </div>
                        </div>
                      )}
                    </div>

                    <div>
                      <div className="text-gray-800 text-sm sm:text-base font-semibold font-['Pretendard'] leading-9 mb-2">
                        업체명
                      </div>
                      <div className="w-full h-12 bg-white rounded-sm outline outline-[0.50px] outline-gray-300 flex items-center px-4">
                        <div className="text-gray-400 text-sm sm:text-base font-medium font-['Pretendard'] leading-relaxed">
                          {userInfo?.organization || '업체명을 입력해주세요'}
                        </div>
                      </div>
                    </div>
                  </div>
                </div>
              </div>

              {/* 추가 요청사항 섹션 */}
              <div className="mt-8">
                <div className="text-gray-800 text-sm sm:text-base font-semibold font-['Pretendard'] leading-9 mb-2">
                  추가 요청사항
                </div>
                <div className="w-full h-36 bg-white rounded-sm outline outline-[0.50px] outline-gray-300">
                  <textarea
                    value={additionalRequest}
                    onChange={(e) => setAdditionalRequest(e.target.value)}
                    placeholder="요청사항을 입력해주세요"
                    className="w-full h-full p-4 text-slate-900 text-sm sm:text-base font-medium font-['Pretendard'] leading-relaxed resize-none focus:outline-none"
                  />
                </div>
              </div>
            </>
          )}

          {/* 2단계: 서류 업로드 */}
          {currentStep === 2 && (
            <div className="mt-8">
              <div className="bg-gray-50 p-4 sm:p-6 md:p-8 rounded border border-black/5">
                <h2 className="text-slate-800 text-lg sm:text-xl font-bold font-['Pretendard'] leading-9 mb-6">
                  필요 서류 안내
                </h2>
                <ul className="list-disc list-inside space-y-4 text-sm sm:text-base text-gray-700">
                  <li>신분증 사본 (주민등록증, 운전면허증 등)</li>
                  <li>재직증명서</li>
                  <li>기타 입주 심사에 필요한 서류</li>

                    <li>
                      입주협력업체의 경우 협력사 계약확인서{' '}
                      <a
                        href="https://futuremnc.mycafe24.com/bnfc/downloads/download.php?file=협력사_계약확인서.pdf"
                        download="협력사_계약확인서.pdf"
                        className="text-blue-500 text-sm font-medium font-['Pretendard'] underline hover:text-blue-600"
                      >
                        (양식 다운로드)
                      </a>
                    </li>
                </ul>
              </div>

              <div className="mt-8 space-y-6 sm:space-y-8">
                <div>
                  <h3 className="text-slate-800 text-base sm:text-lg md:text-xl font-bold font-['Pretendard'] leading-9 mb-4">
                    신분증 사본
                  </h3>
                  <div className="flex items-center space-x-4">
                    <input
                      type="file"
                      accept=".pdf,.jpg,.jpeg,.png"
                      onChange={(e) => {
                        const file = e.target.files?.[0];
                        if (file) handleFileUpload(file, 'identification', e.target);
                      }}
                      className="block w-full text-sm text-gray-500
                        file:mr-4 file:py-2 file:px-4
                        file:rounded-md file:border-0
                        file:text-sm file:font-semibold
                        file:bg-blue-50 file:text-blue-700
                        hover:file:bg-blue-100"
                    />
                  </div>
                  {renderUploadedFileStatus('identification')}
                </div>

                <div>
                  <h3 className="text-slate-800 text-base sm:text-lg md:text-xl font-bold font-['Pretendard'] leading-9 mb-4">
                    재직증명서
                  </h3>
                  <div className="flex items-center space-x-4">
                    <input
                      type="file"
                      accept=".pdf,.jpg,.jpeg,.png"
                      onChange={(e) => {
                        const file = e.target.files?.[0];
                        if (file) handleFileUpload(file, 'employment', e.target);
                      }}
                      className="block w-full text-sm text-gray-500
                        file:mr-4 file:py-2 file:px-4
                        file:rounded-md file:border-0
                        file:text-sm file:font-semibold
                        file:bg-blue-50 file:text-blue-700
                        hover:file:bg-blue-100"
                    />
                  </div>
                  {renderUploadedFileStatus('employment')}
                </div>

                {(userInfo?.userLevel === 6 || selectedOrgType === 6) && (
                  <div>
                    <h3 className="text-slate-800 text-base sm:text-lg md:text-xl font-bold font-['Pretendard'] leading-9 mb-4">
                      협력사 계약확인서
                    </h3>
                    <div className="flex items-center space-x-4">
                      <input
                        type="file"
                        accept=".pdf,.jpg,.jpeg,.png"
                        onChange={(e) => {
                          const file = e.target.files?.[0];
                          if (file) handleFileUpload(file, 'other', e.target);
                        }}
                        className="block w-full text-sm text-gray-500
                          file:mr-4 file:py-2 file:px-4
                          file:rounded-md file:border-0
                          file:text-sm file:font-semibold
                          file:bg-blue-50 file:text-blue-700
                          hover:file:bg-blue-100"
                      />
                    </div>
                    {renderUploadedFileStatus('other')}
                  </div>
                )}

                {!(userInfo?.userLevel === 6 || selectedOrgType === 6) && (
                  <div>
                    <h3 className="text-slate-800 text-base sm:text-lg md:text-xl font-bold font-['Pretendard'] leading-9 mb-4">
                      기타 서류
                    </h3>
                    <div className="flex items-center space-x-4">
                      <input
                        type="file"
                        accept=".pdf,.jpg,.jpeg,.png"
                        onChange={(e) => {
                          const file = e.target.files?.[0];
                          if (file) handleFileUpload(file, 'other', e.target);
                        }}
                        className="block w-full text-sm text-gray-500
                          file:mr-4 file:py-2 file:px-4
                          file:rounded-md file:border-0
                          file:text-sm file:font-semibold
                          file:bg-blue-50 file:text-blue-700
                          hover:file:bg-blue-100"
                      />
                    </div>
                    {renderUploadedFileStatus('other')}
                  </div>
                )}

                {(userInfo?.userLevel === 6 || selectedOrgType === 6) && (
                  <div className="mt-4 text-red-600 text-sm">
                    * 협력사 계약확인서를 반드시 첨부하세요
                  </div>
                )}
              </div>

              {uploading && (
                <div className="mt-4 text-center text-gray-600">
                  파일 업로드 중...
                </div>
              )}

              {uploadError && (
                <div className="mt-4 text-red-600">
                  {uploadError}
                </div>
              )}
            </div>
          )}

          {/* 3단계: 신청 확인 및 제출 */}
          {currentStep === 3 && (
            <div className="mt-8">
              <div className="text-slate-800 text-lg sm:text-xl font-bold font-['Pretendard'] leading-9">
                신청 내용 확인
              </div>
              <div className="mt-4 text-slate-700 text-sm sm:text-base font-normal font-['Pretendard'] leading-relaxed">
                아래 내용으로 포트빌 입주 대기를 신청합니다.
              </div>

              {userInfo && (
                <div className="mt-8 space-y-6 sm:space-y-8">
                  <div>
                    <h3 className="text-slate-800 text-base sm:text-lg md:text-xl font-bold font-['Pretendard'] leading-9 mb-4">
                      신청자 정보
                    </h3>
                    <div className="bg-gray-50 p-4 sm:p-6 md:p-8 rounded border border-black/5">
                      <div className="grid grid-cols-1 sm:grid-cols-2 gap-4 sm:gap-6 md:gap-8">
                        <div>
                          <p className="text-xs sm:text-sm text-gray-500">이름</p>
                          <p className="text-sm sm:text-base font-medium">{userInfo.name}</p>
                        </div>
                        <div>
                          <p className="text-xs sm:text-sm text-gray-500">연락처</p>
                          <p className="text-sm sm:text-base font-medium">{userInfo.phoneNumber}</p>
                        </div>
                        <div className="sm:col-span-2">
                          <p className="text-xs sm:text-sm text-gray-500">이메일</p>
                          <p className="text-sm sm:text-base font-medium">{userInfo.email}</p>
                        </div>
                      </div>
                    </div>
                  </div>

                  <div>
                    <h3 className="text-slate-800 text-base sm:text-lg md:text-xl font-bold font-['Pretendard'] leading-9 mb-4">
                      업체 정보
                    </h3>
                    <div className="bg-gray-50 p-4 sm:p-6 md:p-8 rounded border border-black/5">
                      <div className="grid grid-cols-1 sm:grid-cols-2 gap-4 sm:gap-6 md:gap-8">
                        <div>
                          <p className="text-xs sm:text-sm text-gray-500">업체분류</p>
                          <p className="text-sm sm:text-base font-medium">{getUserLevelText(userInfo.userLevel)}</p>
                        </div>
                        <div>
                          <p className="text-xs sm:text-sm text-gray-500">업체명</p>
                          <p className="text-sm sm:text-base font-medium">{userInfo.organization}</p>
                        </div>
                      </div>
                    </div>
                  </div>

                  {additionalRequest && (
                    <div>
                      <h3 className="text-slate-800 text-base sm:text-lg md:text-xl font-bold font-['Pretendard'] leading-9 mb-4">
                        추가 요청사항
                      </h3>
                      <div className="bg-gray-50 p-4 sm:p-6 md:p-8 rounded border border-black/5">
                        <p className="text-sm sm:text-base text-gray-700">{additionalRequest}</p>
                      </div>
                    </div>
                  )}

                  <div>
                    <h3 className="text-slate-800 text-base sm:text-lg md:text-xl font-bold font-['Pretendard'] leading-9 mb-4">
                      제출 서류
                    </h3>
                    <div className="bg-gray-50 p-4 sm:p-6 md:p-8 rounded border border-black/5">
                      <ul className="space-y-2">
                        {uploadedFiles.map((file, index) => (
                          <li key={index} className="flex items-start sm:items-center">
                            <span className="text-green-600 mr-2 mt-0.5 sm:mt-0">✓</span>
                            <div className="flex flex-col sm:flex-row sm:items-center flex-1">
                              <span className="text-sm sm:text-base font-medium">
                                {file.type === 'identification' && '신분증 사본'}
                                {file.type === 'employment' && '재직증명서/사업자등록증'}
                                {file.type === 'other' && '기타 서류'}
                              </span>
                              <span className="text-xs sm:text-sm text-gray-500 sm:ml-2 break-all">({file.name})</span>
                            </div>
                          </li>
                        ))}
                      </ul>
                    </div>
                  </div>
                </div>
              )}

              {!storageAvailable && (
                <div className="mt-8 bg-yellow-50 border-l-4 border-yellow-400 p-4 rounded">
                  <p className="text-yellow-700">
                    <span className="font-bold">주의:</span> 개발 환경에서는 파일 업로드가 실제로 처리되지 않습니다.
                  </p>
                </div>
              )}
            </div>
          )}

          {/* 하단 여백 */}
          <div className="h-32"></div>

          {/* 하단 버튼 섹션 */}
          <div className="fixed bottom-0 left-0 w-full h-20 sm:h-24 bg-white border-t border-gray-200 z-10">
            <div className="max-w-3xl mx-auto px-4 sm:px-6 lg:px-8 h-full flex items-center justify-end gap-2 sm:gap-4">
              {currentStep > 1 && (
                <button
                  onClick={handlePrevious}
                  className="w-32 sm:w-44 h-12 sm:h-16 rounded-sm border border-gray-300 text-gray-700 hover:bg-gray-50"
                >
                  <div className="text-center text-gray-700 text-base sm:text-lg font-bold font-['Pretendard'] leading-9">
                    이전
                  </div>
                </button>
              )}
              <button
                onClick={currentStep === 3 ? handleSubmit : handleNext}
                disabled={
                  (currentStep === 1 && !isFirstStepValid()) ||
                  (currentStep === 2 && !isSecondStepValid()) ||
                  submitting
                }
                className={`w-32 sm:w-44 h-12 sm:h-16 ${
                  ((currentStep === 1 && isFirstStepValid()) ||
                   (currentStep === 2 && isSecondStepValid()) ||
                   currentStep === 3) && !submitting
                    ? 'bg-blue-500 hover:bg-blue-600'
                    : 'bg-gray-400 cursor-not-allowed'
                } rounded-sm`}
              >
                <div className="text-center text-white text-base sm:text-lg font-bold font-['Pretendard'] leading-9">
                  {currentStep === 3 ? (submitting ? '제출 중...' : '신청하기') : '다음단계'}
                </div>
              </button>
            </div>
          </div>
        </div>
      </div>
    </ClientLayout>
  );
};

export default WaitingPage; 
